/** Canonical keys for budget namespace metafields (lowercase; matches draft-order auto-approve). */
export const SITE_BUDGET_CATEGORY_KEYS = [
  "materials",
  "consumables",
  "workwear",
  "machinery",
  "miscellaneous",
] as const;

export type SiteBudgetCategoryKey = (typeof SITE_BUDGET_CATEGORY_KEYS)[number];

export type SiteBudgetAllocations = Record<SiteBudgetCategoryKey, number>;

export type SiteBudgetRolloverFrequency =
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annual";

export type TSiteBudgetManagement = {
  enabled: boolean;
  budgetStartDate: string | null;
  rolloverFrequency: SiteBudgetRolloverFrequency | null;
  rolloverEnabled: boolean;
  rolloverPeriods: number;
  allocations: SiteBudgetAllocations;
  /** @deprecated Remaining balances are sourced from BC, not persisted on save. */
  remaining?: Partial<Record<SiteBudgetCategoryKey, number>>;
  updatedAt?: string;
  updatedByUserId?: string;
  lastShopifySyncAt?: string | null;
  lastShopifySyncError?: string | null;
  /** Start of the active budget period (YYYY-MM-DD); updated by rollover cron. */
  currentPeriodStart?: string | null;
  /** When the cron last applied a period rollover (ISO timestamp). */
  lastRolloverAt?: string | null;
};

export const SITE_BUDGET_CATEGORY_LABELS: Record<SiteBudgetCategoryKey, string> = {
  materials: "Materials",
  consumables: "Consumables",
  workwear: "Workwear",
  machinery: "Machinery",
  miscellaneous: "Misc",
};

export function emptyAllocations(): SiteBudgetAllocations {
  return {
    materials: 0,
    consumables: 0,
    workwear: 0,
    machinery: 0,
    miscellaneous: 0,
  };
}

export function defaultSiteBudgetManagement(): TSiteBudgetManagement {
  return {
    enabled: false,
    budgetStartDate: null,
    rolloverFrequency: "monthly",
    rolloverEnabled: true,
    rolloverPeriods: 1,
    allocations: emptyAllocations(),
    remaining: {},
  };
}

export function parseSiteBudgetManagement(raw: unknown): TSiteBudgetManagement {
  const base = defaultSiteBudgetManagement();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  const allocations = { ...base.allocations };
  for (const k of SITE_BUDGET_CATEGORY_KEYS) {
    const n = Number((o.allocations as any)?.[k]);
    if (Number.isFinite(n) && n >= 0) allocations[k] = n;
  }
  let remaining: Partial<Record<SiteBudgetCategoryKey, number>> = {};
  if (o.remaining && typeof o.remaining === "object") {
    for (const k of SITE_BUDGET_CATEGORY_KEYS) {
      const n = Number((o.remaining as any)[k]);
      if (Number.isFinite(n) && n >= 0) remaining[k] = n;
    }
  }
  const rf = o.rolloverFrequency;
  const rolloverFrequency =
    rf === "weekly" || rf === "monthly" || rf === "quarterly" || rf === "annual"
      ? rf
      : base.rolloverFrequency;
  const rp = Number(o.rolloverPeriods);
  return {
    enabled: Boolean(o.enabled),
    budgetStartDate: typeof o.budgetStartDate === "string" ? o.budgetStartDate : base.budgetStartDate,
    rolloverFrequency,
    rolloverEnabled: o.rolloverEnabled !== false,
    rolloverPeriods: Number.isFinite(rp) && rp >= 1 ? Math.min(12, Math.floor(rp)) : base.rolloverPeriods,
    allocations,
    remaining,
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : undefined,
    updatedByUserId: typeof o.updatedByUserId === "string" ? o.updatedByUserId : undefined,
    lastShopifySyncAt: typeof o.lastShopifySyncAt === "string" ? o.lastShopifySyncAt : null,
    lastShopifySyncError:
      typeof o.lastShopifySyncError === "string" ? o.lastShopifySyncError : null,
    currentPeriodStart:
      typeof o.currentPeriodStart === "string" ? o.currentPeriodStart : base.currentPeriodStart ?? null,
    lastRolloverAt: typeof o.lastRolloverAt === "string" ? o.lastRolloverAt : undefined,
  };
}
